import { X, AlertTriangle, MapPin, Clock } from 'lucide-react';
import type { Vehicle } from '../../types/vehicle';
import { hasLivePosition } from '../../types/vehicle';
import { VehicleStatusBadge } from './VehicleListItem';
import { DriverScoreBadge } from '../drivers/RankingTable';
import { VehicleCameraViewer } from './VehicleCameraViewer';
import { formatDate, formatSpeed } from '../../utils/formatters';
import { useDrivers } from '../../hooks/useDrivers';
import { useSheetDrag } from '../../hooks/useSheetDrag';
import { useAuth } from '../../context/AuthContext';

interface VehicleDetailPanelProps {
  vehicle: Vehicle;
  onClose: () => void;
  /** Solo relevante en mobile (bottom-sheet); el gesto y el toque lo cambian. */
  isExpanded?: boolean;
  onSetExpanded?: (expanded: boolean) => void;
}

export function VehicleDetailPanel({ vehicle, onClose, isExpanded = false, onSetExpanded }: VehicleDetailPanelProps) {
  // El score real (o su ausencia) sale de `useDrivers()`, igual que en
  // Drivers/Dashboard/Operator — antes esta pantalla mostraba "92/100"
  // fijo para cualquier conductor.
  const { drivers } = useDrivers();
  const driver = drivers.find(d => d.id === vehicle.driverId);

  // FE-14: el Operador no tiene permiso para ver video en vivo (PRODUCT.md
  // §4.8) — el botón ni siquiera se ofrece, no solo se deshabilita.
  const { user } = useAuth();
  const canViewVideo = user?.role !== 'operator';

  // El sheet móvil solo tenía un `onClick` sobre una barrita de 36x4px: el
  // gesto de deslizar —lo primero que hace cualquiera en un teléfono— no
  // existía, y en "peek" el sheet es `overflow:hidden`, así que tampoco había
  // scroll que diera la sensación de subirlo. Ahora la zona agarrable es el
  // handle MÁS el header, y responde a arrastre y a toque.
  const dragProps = useSheetDrag({
    isExpanded,
    onExpand: () => onSetExpanded?.(true),
    onCollapse: () => onSetExpanded?.(false),
    onDismiss: onClose,
    onTap: () => {
      if (onSetExpanded) onSetExpanded(!isExpanded);
      else onClose();
    },
  });

  return (
    <div className="vehicle-panel vehicle-panel--mobile-sheet">
      {/* Zona agarrable del sheet móvil: arrastrar arriba expande, abajo
          colapsa (y desde "peek" cierra). Un toque alterna, como antes. */}
      <div className="vehicle-panel__grabber" {...dragProps}>
        <div
          className="vehicle-panel__drag-handle"
          role="button"
          tabIndex={0}
          aria-expanded={isExpanded}
          aria-label={isExpanded ? 'Contraer detalle del vehículo' : 'Expandir detalle del vehículo'}
          onKeyDown={e => {
            if (e.key !== 'Enter' && e.key !== ' ') return;
            e.preventDefault();
            if (onSetExpanded) onSetExpanded(!isExpanded);
            else onClose();
          }}
        >
          <div className="vehicle-panel__drag-handle-bar" />
        </div>

        {/* Header */}
        <div className="vehicle-panel__header">
          <div>
            <h2 className="vehicle-panel__plate">{vehicle.plate}</h2>
            <p className="vehicle-panel__model">{vehicle.brand} {vehicle.model} ({vehicle.year})</p>
          </div>
          <button className="vehicle-panel__close" onClick={onClose} aria-label="Cerrar panel" data-sheet-no-drag>
            <X size={20} />
          </button>
        </div>
      </div>

      <div className="vehicle-panel__body" style={{ display: 'flex', flexDirection: 'column', gap: '1rem', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <VehicleStatusBadge status={vehicle.status} />
          <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: 600 }}>Unidad: {vehicle.subUnitName}</span>
        </div>

        {/* Driver & Safety Score Ring */}
        {vehicle.driverName && (
          <div style={{ background: 'var(--bg-raised)', padding: '0.875rem 1rem', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'linear-gradient(135deg, var(--accent), #6366F1)', color: '#FFF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '14px' }}>
                {vehicle.driverName.charAt(0)}
              </div>
              <div>
                <p style={{ margin: 0, fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)' }}>{vehicle.driverName}</p>
                <p style={{ margin: 0, fontSize: '11px', color: 'var(--text-muted)' }}>Conductor Asignado</p>
              </div>
            </div>

            {/* Score real (o "Pendiente" si el conductor no salió en el ranking) */}
            <DriverScoreBadge score={driver?.score} size="sm" />
          </div>
        )}

        {/* Position */}
        <div className="vehicle-panel__row">
          <MapPin size={16} className="vehicle-panel__row-icon" />
          <div>
            <p className="vehicle-panel__row-label">Posición Telemetría</p>
            {hasLivePosition(vehicle.position) ? (
              <>
                <p className="vehicle-panel__row-value">
                  {vehicle.position.lat.toFixed(5)}, {vehicle.position.lng.toFixed(5)}
                </p>
                {vehicle.status === 'in_route' && vehicle.position.speed !== undefined && (
                  <p className="vehicle-panel__row-speed">{formatSpeed(vehicle.position.speed)}</p>
                )}
              </>
            ) : (
              <p className="vehicle-panel__row-value" style={{ color: 'var(--text-muted)' }}>
                Esperando la primera señal del equipo.
              </p>
            )}
          </div>
        </div>

        {/* Last seen */}
        <div className="vehicle-panel__row">
          <Clock size={16} className="vehicle-panel__row-icon" />
          <div>
            <p className="vehicle-panel__row-label">Última señal GPS</p>
            <p className="vehicle-panel__row-value">{formatDate(vehicle.lastSeen)}</p>
          </div>
        </div>

        {/* Active alerts */}
        {vehicle.activeAlerts > 0 && (
          <div className="vehicle-panel__alerts-warning">
            <AlertTriangle size={16} />
            <span>{vehicle.activeAlerts} alerta{vehicle.activeAlerts > 1 ? 's' : ''} activa{vehicle.activeAlerts > 1 ? 's' : ''} en tiempo real</span>
          </div>
        )}

        {/* Video en vivo (FE-14) — el Operador no ve esta sección en absoluto.
            `floating`: Monitoreo quiere el reproductor grande, flotando sobre
            la pantalla — el look original de este panel. */}
        {canViewVideo && (
          <VehicleCameraViewer vehicleId={vehicle.id} plate={vehicle.plate} floating />
        )}
      </div>
    </div>
  );
}
